import {
    AGENTEXCHANGE_JP_SITE_URL,
    AGENTEXCHANGE_SITE_URL,
    APPX_JP_SITE_URL,
    APPX_SITE_URL,
    TBID_LOGIN_AUTH_PROVIDER,
    TBID_SIGNUP_AUTH_PROVIDER,
    TBID_URL,
} from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { getCurrentLocalePrefix } from '~/modules/helpers/localeHelper';
import { ListingResponse } from '~/modules/services/listing_service';
import { siteConfigStore } from '~/modules/stores/siteConfigStore';
import { http } from '~/modules/utilities/http';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { generateListingUrl } from '~/modules/utilities/listing_utils';
import { upsertQueryParams } from '~/modules/utilities/router';

export function generateBaseUrl(): string {
    const isAgentExchange = siteConfigStore.getState().isAgentExchange;
    if (isAgentExchange) {
        const base = getUserLanguage() === JAPANESE_LANGUAGE_CODE ? AGENTEXCHANGE_JP_SITE_URL : AGENTEXCHANGE_SITE_URL;
        return base || AGENTEXCHANGE_SITE_URL;
    }
    if (getUserLanguage() === JAPANESE_LANGUAGE_CODE) {
        return APPX_JP_SITE_URL || APPX_SITE_URL;
    }
    return APPX_SITE_URL;
}

const generateIisAuthenticationUrl = (path: 'login' | 'signup'): string => {
    const prefix = getCurrentLocalePrefix();
    const startURL = encodeURIComponent(window.location.pathname + window.location.search);
    return `${prefix}/${path}?startURL=${startURL}`;
};

export const generateIisLoginUrl = (): string => generateIisAuthenticationUrl('login');

export const generateIisSignupUrl = (): string => generateIisAuthenticationUrl('signup');

export function login(): void {
    if (FeatureFlags.FF_IIS_JS_V2) {
        window.location.href = generateIisLoginUrl();
    } else if (typeof window.SFIDWidget !== 'undefined') {
        window.SFIDWidget.login();
    } else {
        console.warn('login() called but SFIDWidget is not available');
    }
}

export function signup(): void {
    if (FeatureFlags.FF_IIS_JS_V2) {
        window.location.href = generateIisSignupUrl();
    } else if (typeof window.SFIDWidget !== 'undefined') {
        window.SFIDWidget.signup();
    } else {
        console.warn('signup() called but SFIDWidget is not available');
    }
}

export function logout(): void {
    if (FeatureFlags.FF_IIS_JS_V2) {
        window.location.href = generateIisLogoutUrl();
    } else if (typeof window.SFIDWidget !== 'undefined') {
        window.SFIDWidget.logout();
    }
}

function generateIisLogoutUrl(): string {
    const currentUrl = window.location.origin + window.location.pathname + window.location.search;
    const redirectParam = `post_logout_redirect_uri=${encodeURIComponent(currentUrl)}`;
    const baseUrl = window.iis?.getAuthLogoutUrl() || `${TBID_URL}/services/auth/logout`;
    return `${baseUrl}?${redirectParam}`;
}

function generateTbidBaseUrl(): string {
    const isAgentExchange = siteConfigStore.getState().isAgentExchange;
    if (isAgentExchange) {
        // LoginPortal and SignupPortal live on the org, not the locale-prefixed SPA URL.
        // AGENTEXCHANGE_JP_SITE_URL includes a /jp path that would produce
        // /jp/services/auth/sso/LoginPortal — a path Fastly routes to Heroku, not the org.
        return AGENTEXCHANGE_SITE_URL;
    }
    return generateBaseUrl();
}

export function generateTbidLoginUrl(): string {
    return `${generateTbidBaseUrl()}${TBID_LOGIN_AUTH_PROVIDER}`;
}

export function generateTbidSignUpUrl(): string {
    return `${generateTbidBaseUrl()}${TBID_SIGNUP_AUTH_PROVIDER}`;
}

export function generateLegacyListingUrl(listing?: ListingResponse | null) {
    const url = generateListingUrl(
        generateBaseUrl(),
        listing,
        upsertQueryParams(['legacy', 'true']),
        getCurrentLocalePrefix(),
    );
    return url;
}

type FooterAndHeaderEndpoint = 'nav' | 'footer';

function generateFooterAndHeaderUrl(endpoint: FooterAndHeaderEndpoint) {
    // we only want the extra explore flag for nav
    if (endpoint === 'nav') {
        return `${generateBaseUrl()}/${endpoint}?explore`;
    }
    return `${generateBaseUrl()}/${endpoint}`;
}

export async function fetchFooterAndHeaderContent(endpoint: FooterAndHeaderEndpoint) {
    // Evaluate the URL outside the try so a synchronous throw from generateBaseUrl()
    // surfaces rather than being silently swallowed by the catch below.
    const url = generateFooterAndHeaderUrl(endpoint);
    try {
        // W-12473405: Remove the correlationId header that is causing CORS issues when running in localhost.
        // It's not useful to track failures in this endpoint by logging the correlation ID anyway.
        const response = await http.get(url, {
            includeCorrelationIdHeader: false,
        });
        const responseData = response.data;
        return responseData;
    } catch {
        // http.ts calls handleException before re-throwing on HTTP errors — no second log needed.
        // Pre-axios synchronous throws from http.fetch() (e.g. UUID generation) reach here unlogged,
        // but are implementation exceptions too rare to warrant a fallback log.
        return '';
    }
}
